import React from 'react';

import { ArticlePickerDialog, ArticlePickerDialogProps } from '@lumapps/article-pickers/components/ArticlePickerDialog';
import { getArticlesByIds } from '@lumapps/articles/api/getArticlesByIds';
import { Article } from '@lumapps/articles/api/types';
import { ARTICLES } from '@lumapps/articles/keys';
import { SelectionSetting, SelectionSettingProps } from '@lumapps/designer-widget-settings/components/SelectionSetting';
import { SelectionSettingType } from '@lumapps/designer-widget-settings/types';
import { Text } from '@lumapps/lumx/react';
import { makeSecuredMediaURLRelative } from '@lumapps/medias/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { DATA_SCOPE, WIDGET_ARTICLE_LIST_ICON } from '../../../constants';

export interface SelectionSectionProps {
    currentSelection?: SelectionSettingType;
    onChange: (properties: { selection: SelectionSettingType }) => void;
}

const CLASSNAME = 'selection-section';

export const SelectionSection: React.FC<SelectionSectionProps> = ({
    currentSelection: widgetCurrentSelection,
    onChange,
}) => {
    const { translateKey } = useTranslate();

    const entityBlock = React.useMemo(
        (): SelectionSettingProps<Article, any>['entityBlock'] => ({
            title: (article) => article.structuredContent.title?.value as string,
            // eslint-disable-next-line react/no-unstable-nested-components
            subtitle: (article) => (
                <Text as="p" typography="body1" color="dark-L2" truncate>
                    {`${translateKey(GLOBAL.IN)} ${article.site.name} > ${article.communityReference?.name.value}`}
                </Text>
            ),
            icon: WIDGET_ARTICLE_LIST_ICON,
            image: (article) =>
                makeSecuredMediaURLRelative(article.structuredContent.richText?.value?.featuredImage?.url),
        }),
        [translateKey],
    );

    const handleOnChange = React.useCallback(
        (selection: SelectionSettingType) => {
            onChange({ selection });
        },
        [onChange],
    );

    return (
        <SelectionSetting<Article, ArticlePickerDialogProps>
            entityBlock={entityBlock}
            entityPicker={{
                component: ArticlePickerDialog,
                props: { isMultipleSelect: true, context: 'platform', initParams: { communityIds: undefined } },
            }}
            manualButtonText={ARTICLES.SELECT_ARTICLES}
            onChange={handleOnChange}
            getEntities={getArticlesByIds}
            currentSelection={widgetCurrentSelection}
            dataScope={`${DATA_SCOPE}-selection`}
            className={CLASSNAME}
        />
    );
};
